import { doc, getDoc, updateDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase/config';

/**
 * Fix application records that are missing businessId or appliedAt
 * This can be called from the browser console
 */
export const fixBrokenApplications = async () => {
  try {
    console.log('🔧 Starting application fix process...');
    
    // Get all applications
    const applicationsSnapshot = await getDocs(collection(firestore, 'jobApplications'));
    const allApplications = applicationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📋 Found ${allApplications.length} total applications`);
    
    // Find applications missing businessId or appliedAt
    const brokenApplications = allApplications.filter(app => 
      !app.businessId || !app.appliedAt
    );
    
    console.log(`🔍 Found ${brokenApplications.length} applications that need fixing:`, brokenApplications);
    
    if (brokenApplications.length === 0) {
      console.log('✅ No broken applications found!');
      return;
    }
    
    // Fix each broken application
    for (const app of brokenApplications) {
      try {
        console.log(`🔧 Fixing application ${app.id}...`);
        
        const updateData = {};
        
        // Add missing appliedAt field
        if (!app.appliedAt) {
          updateData.appliedAt = app.createdAt || app.updatedAt || new Date();
          console.log(`  📅 Adding appliedAt: ${updateData.appliedAt}`);
        }
        
        // Add missing businessId by looking up the job
        if (!app.businessId && app.jobId) {
          console.log(`  🔍 Looking up job ${app.jobId} to get businessId...`);
          const jobDoc = await getDoc(doc(firestore, 'jobs', app.jobId));
          
          if (jobDoc.exists()) {
            const jobData = jobDoc.data();
            if (jobData.businessId) {
              updateData.businessId = jobData.businessId;
              console.log(`  🏢 Found businessId: ${updateData.businessId}`);
            } else {
              console.warn(`  ⚠️ Job ${app.jobId} has no businessId`);
            }
          } else {
            console.warn(`  ⚠️ Job ${app.jobId} not found`);
          }
        }
        
        // Add id field if missing
        if (!app.id) {
          updateData.id = app.id;
        }
        
        // Update the application if we have data to update
        if (Object.keys(updateData).length > 0) {
          await updateDoc(doc(firestore, 'jobApplications', app.id), updateData);
          console.log(`  ✅ Updated application ${app.id} with:`, updateData);
        } else {
          console.log(`  ⚠️ No updates needed for application ${app.id}`);
        }
        
      } catch (error) {
        console.error(`  ❌ Error fixing application ${app.id}:`, error);
      }
    }
    
    console.log('🎉 Application fix process completed!');
    
  } catch (error) {
    console.error('❌ Error in fix process:', error);
  }
};

/**
 * Fix a specific application by ID
 */
export const fixSpecificApplication = async (applicationId) => {
  try {
    console.log(`🔧 Fixing specific application: ${applicationId}`);
    
    const appDoc = await getDoc(doc(firestore, 'jobApplications', applicationId));
    if (!appDoc.exists()) {
      console.error('❌ Application not found');
      return;
    }
    
    const appData = appDoc.data();
    console.log('📋 Current application data:', appData);
    
    const updateData = {};
    
    // Add missing appliedAt
    if (!appData.appliedAt) {
      updateData.appliedAt = appData.createdAt || appData.updatedAt || new Date();
    }
    
    // Add missing businessId
    if (!appData.businessId && appData.jobId) {
      const jobDoc = await getDoc(doc(firestore, 'jobs', appData.jobId));
      if (jobDoc.exists()) {
        const jobData = jobDoc.data();
        if (jobData.businessId) {
          updateData.businessId = jobData.businessId;
        }
      }
    }
    
    // Add id field
    if (!appData.id) {
      updateData.id = applicationId;
    }
    
    if (Object.keys(updateData).length > 0) {
      await updateDoc(doc(firestore, 'jobApplications', applicationId), updateData);
      console.log('✅ Application fixed with:', updateData);
    } else {
      console.log('ℹ️ Application doesn\'t need fixing');
    }
    
  } catch (error) {
    console.error('❌ Error fixing application:', error);
  }
};

// Make functions available globally for console access
if (typeof window !== 'undefined') {
  window.fixBrokenApplications = fixBrokenApplications;
  window.fixSpecificApplication = fixSpecificApplication;
} 